// Console + uncaught error / unhandled rejection capture.
// - Skips the widget's own '[ff]'-prefixed lines (no feedback loop into the payload).
// - Formats arguments cheaply: never full-stringifies big objects (a console.log(hugeState)
//   in a render loop must not jank the host app while recording).
// - Defensive teardown: restore only if still our wrapper; orphaned wrappers go inert.

const MAX_MSG = 500;

export function startConsole(push) {
  let live = true;
  const levels = ['log', 'info', 'warn', 'error'];
  const orig = {};
  const wrapped = {};

  levels.forEach((level) => {
    orig[level] = console[level];
    wrapped[level] = function (...args) {
      if (live && !(typeof args[0] === 'string' && args[0].startsWith('[ff]'))) {
        push({ type: 'console', level, message: args.map(preview).join(' ').slice(0, MAX_MSG) });
      }
      return orig[level].apply(console, args);
    };
    console[level] = wrapped[level];
  });

  const onError = (e) => { if (live) push({ type: 'console', level: 'error', message: String(e.message || 'error').slice(0, MAX_MSG), stack: e.error && e.error.stack && String(e.error.stack).slice(0, 1500) }); };
  const onRejection = (e) => { if (live) push({ type: 'console', level: 'error', message: ('unhandledrejection: ' + preview(e.reason && e.reason.message ? e.reason.message : e.reason)).slice(0, MAX_MSG) }); };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    live = false;
    levels.forEach((level) => { if (console[level] === wrapped[level]) console[level] = orig[level]; });
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

// Cheap, budget-bound preview of an arbitrary value — no full JSON.stringify of large graphs.
function preview(a, budget = MAX_MSG) {
  if (typeof a === 'string') return a.slice(0, budget);
  if (a === null || a === undefined || typeof a === 'number' || typeof a === 'boolean') return String(a);
  if (a instanceof Error) return String(a).slice(0, budget);
  if (Array.isArray(a)) return `[Array(${a.length})]`;
  if (typeof a === 'object') {
    const parts = [];
    let used = 2;
    for (const k of Object.keys(a)) {
      const v = a[k];
      const piece = `${k}: ${typeof v === 'object' && v !== null ? (Array.isArray(v) ? `[Array(${v.length})]` : '{…}') : String(v).slice(0, 50)}`;
      if (used + piece.length > budget) { parts.push('…'); break; }
      parts.push(piece);
      used += piece.length + 2;
    }
    return `{${parts.join(', ')}}`;
  }
  return String(a).slice(0, budget);
}
